const { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } = require('../constants');

const LANGUAGES = {
  pt: 'Português',
  en: 'English',
  es: 'Español'
};

const TRANSLATIONS = {
  pt: {
    locale: 'pt-BR',
    appTitle: 'Randomização para ensaios clínicos',
    darkMode: 'Modo escuro',
    lightMode: 'Modo claro',
    validationReportLink: 'Versão 2 Validada',
    userManualLink: 'Manual',
    steps: ['Estudo', 'Técnica', 'Parâmetros', 'Resultado'],
    study: {
      title: 'Definição do estudo',
      description: 'Configure a base do protocolo antes de escolher o método de randomização.',
      name: 'Nome do estudo',
      namePlaceholder: 'Ex.: Ensaio Clínico Tese 2026',
      participants: 'Número de participantes',
      arms: 'Número de braços',
      armsOptions: ['2 braços', '3 braços', '4 braços'],
      armNames: 'Nomes dos braços',
      armDescription: 'Use nomes claros para aparecerem na lista, exports e relatório.',
      armName: 'Nome do braço',
      clear: 'Limpar',
      next: 'Avançar',
      back: 'Voltar'
    },
    technique: {
      title: 'Técnica de randomização',
      description: 'Escolha primeiro o que será randomizado. Depois selecione a técnica com base na unidade de randomização e balanceamento.',
      assisted: 'Escolha assistida',
      filters: {
        all: 'Todos',
        participant: 'Participante',
        stratified: 'Participante + estrato',
        cluster: 'Cluster inteiro'
      },
      families: {
        basic: ['Básica', 'Sorteio individual'],
        blocks: ['Blocos', 'Equilíbrio durante a inclusão'],
        stratified: ['Estratificada', 'Equilíbrio dentro de subgrupos'],
        cluster: ['Cluster', 'Um tratamento por grupo']
      }
    },
    parameters: {
      title: 'Parâmetros e reprodutibilidade',
      description: 'O seed é a chave para reproduzir exatamente a mesma lista em outro computador.',
      seed: 'Seed',
      generateSeed: 'Gerar seed',
      copySeed: 'Copiar seed',
      blockSize: 'Tamanho do bloco',
      blockSizes: 'Tamanhos permitidos de bloco',
      clusterSizes: 'Tamanhos dos clusters',
      strata: 'Estratos',
      clusterStrata: 'Clusters como estratos',
      clusterStrataSizes: 'Clusters dentro de estratos',
      csvTitle: 'Importação por CSV',
      csvDescription: 'Use templates para preencher clusters, estratos ou clusters dentro de estratos com menos erro manual.',
      strataColumns: 'Características para estratificar',
      strataColumnsHelp: 'Para métodos estratificados, importe uma lista por participante. No preview, marque as colunas que formarão os estratos compostos.',
      downloadTemplate: 'Baixar template CSV',
      importCsv: 'Importar CSV',
      csvPreview: 'Preview do CSV',
      csvCheck: 'Conferência antes de aplicar',
      discard: 'Descartar',
      applyCsv: 'Aplicar CSV',
      generate: 'Gerar randomização'
    },
    results: {
      title: 'Resultado e verificação',
      description: 'Exporte a lista e gere o relatório para conferência metodológica.',
      technique: 'Técnica',
      imbalance: 'Imbalance',
      validation: 'Validação',
      auditEvents: 'Audit events',
      chainHead: 'Chain head',
      auditNote: 'Trilha local encadeada por hash para preparação regulatória V2.',
      armDistribution: 'Distribuição por braço',
      arm: 'Braço',
      warnings: 'Avisos',
      noWarnings: 'No validation warnings.',
      visibleSolution: 'Modo solução visível',
      patient: 'Paciente',
      order: 'Ordem',
      block: 'Bloco',
      cluster: 'Cluster',
      stratum: 'Estrato',
      methods: 'Método',
      audit: 'Auditoria',
      manifest: 'Manifesto',
      signature: 'Assinatura',
      report: 'Relatório PDF',
      sequencePreviewNote: 'Mostrando apenas as primeiras {shown} linhas de {total} na página para manter a navegação leve. A lista completa permanece disponível nos exports CSV, JSON, R, Python e relatório.',
      largeNWarning: 'Para N acima de 2.000, a lista pode ficar longa no navegador. Use os exports para a lista completa; virtual scroll deve entrar no próximo endurecimento de UI.'
    },
    validation: {
      passTitle: 'PASS - lista pronta para revisão',
      passBody: 'Nenhum alerta metodológico foi detectado. Exporte a lista e confira assinatura, relatório e parâmetros antes de usar.',
      warnTitle: 'WARN - revisar antes de usar',
      warnBody: 'A lista foi gerada, mas há alerta metodológico. Revise os avisos; mudar a seed só ajuda quando o problema não é estrutural.'
    },
    localData: {
      eyebrow: 'Persistência local',
      title: 'Dados locais e fila offline',
      summary: 'Rascunho, último resultado e eventos offline ficam apenas neste navegador.',
      draft: 'Rascunho',
      lastResult: 'Último resultado',
      queue: 'Fila offline',
      saved: 'Salvo',
      empty: 'Vazio',
      events: 'eventos',
      exportQueue: 'Exportar fila',
      clearQueue: 'Limpar fila',
      clearLocalData: 'Limpar dados locais',
      queueCleared: 'Fila offline limpa.',
      localDataCleared: 'Rascunho e último resultado locais foram limpos.',
      queueExported: 'Fila offline exportada.'
    },
    operational: {
      securityEyebrow: 'Segurança operacional',
      modeTitle: 'Modo de uso',
      modeDescription: 'Use planejamento para visualizar a lista completa. Use operacional para ocultar alocações futuras durante o enrolamento.',
      planningLabel: 'Educacional / planejamento',
      planningHelp: 'Lista completa visível após gerar.',
      operationalLabel: 'Operacional protegido',
      operationalHelp: 'Alocações futuras ficam ocultas e cada revelação é auditada.',
      eyebrow: 'Allocation concealment',
      title: 'Revelação progressiva',
      description: 'A lista completa existe internamente, mas a interface mostra apenas alocações já reveladas.',
      revealed: 'Reveladas',
      concealed: 'Ocultas',
      seedCommit: 'Seed commit',
      revealNext: 'Revelar próxima alocação',
      copySeedCommit: 'Copiar seed commit',
      exportOperational: 'Export operacional',
      exportIntegrity: 'Manifesto de hashes',
      concealedCell: 'Oculto até enrolamento',
      fullExportBlocked: 'Modo operacional ativo: export da lista completa bloqueado enquanto houver alocações ocultas. Use Export operacional.',
      allocationRevealed: 'Alocação revelada e registrada na auditoria.',
      allRevealed: 'Todas as alocações já foram reveladas.',
      commitCopied: 'Seed commit copiado. Registre externamente antes do enrolamento.',
      commitInstructions: 'Registre este hash fora do randFlow antes do enrolamento: e-mail institucional datado, OSF, ClinicalTrials.gov ou repositório aprovado pelo estudo.',
      verificationEyebrow: 'Verificação independente',
      verificationTitle: 'Reproduzir assinatura',
      verificationDescription: 'Cole um JSON com técnica, parâmetros e assinatura esperada para regerar a lista sem depender do estado do navegador.',
      fillVerification: 'Preencher com resultado atual',
      runVerification: 'Verificar',
      verificationPass: 'PASS - assinatura reproduzida.',
      verificationFail: 'FAIL - assinatura divergente.',
      verificationError: 'Não foi possível verificar o JSON informado.',
      sessionStorageNote: 'No modo operacional, o último resultado é preservado apenas na sessão atual e não no armazenamento local persistente.'
    },
    status: {
      generated: 'Randomização gerada com sucesso.',
      signatureCopied: 'Assinatura do experimento copiada.',
      seedCopied: 'Seed copiado.',
      csvLoaded: 'CSV carregado para preview. Revise a tabela antes de aplicar.',
      csvApplied: 'CSV aplicado aos campos do método ativo.',
      restoredDraft: 'Rascunho local restaurado.',
      restoredResult: 'Último resultado local restaurado.',
      languageChanged: 'Idioma alterado.',
      needCsv: 'Importe um CSV antes de aplicar.',
      csvUnavailable: 'Importação CSV está disponível apenas para métodos com clusters ou estratos.',
      needResult: 'Gere uma randomização antes de exportar.',
      participantRange: 'Use um número de participantes entre 1 e 10000.',
      armRange: 'Use entre 2 e 4 braços.'
    },
    preview: {
      selectedMethod: 'Método selecionado',
      randomizes: 'Randomiza',
      balances: 'Balanceia',
      whenUse: 'Quando usar',
      caution: 'Cuidado',
      methodPreview: 'Preview metodológico',
      participants: 'Participantes',
      arms: 'Braços',
      randomizedUnit: 'Unidade randomizada',
      balance: 'Balanceamento',
      pendingSeed: 'Seed pendente',
      currentSeed: 'Seed atual'
    },
    techniques: {
      simple: ['Simples', 'Sorteia cada participante de forma independente. Pode desequilibrar em amostras pequenas.', 'Participante', 'Não força balanceamento'],
      blocked: ['Blocos fixos', 'Mantém equilíbrio a cada bloco completo. Mais simples, porém mais previsível.', 'Participante', 'Bloco fixo'],
      'random-block': ['Blocos aleatórios', 'Equilibra por blocos, variando o tamanho para reduzir previsibilidade operacional.', 'Participante', 'Bloco variável'],
      'stratified-blocked': ['Estratificada por blocos fixos', 'Repete blocos fixos dentro de cada estrato, como centro, sexo ou gravidade.', 'Participante', 'Estrato + bloco fixo'],
      'stratified-random-block': ['Estratificada por blocos aleatórios', 'Repete blocos variáveis dentro de cada estrato para balancear com menor previsibilidade.', 'Participante', 'Estrato + bloco variável'],
      'cluster-stratum': ['Cluster como estrato', 'O centro organiza o balanceamento, mas participantes do mesmo centro podem receber braços diferentes.', 'Participante', 'Centro/cluster'],
      clustered: ['Cluster', 'Sorteia grupos intactos. Todos os participantes do cluster recebem o mesmo braço.', 'Cluster', 'Quantidade de clusters'],
      'cluster-stratified': ['Cluster estratificado', 'Sorteia clusters inteiros dentro de estratos, preservando um único braço por cluster.', 'Cluster', 'Estrato de clusters']
    },
    tags: {
      randomizes: 'Randomiza',
      balances: 'Balanceia'
    }
  },
  en: {
    locale: 'en',
    appTitle: 'Randomization for clinical trials',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    validationReportLink: 'Validated Version 2',
    userManualLink: 'Manual',
    steps: ['Study', 'Technique', 'Parameters', 'Result'],
    study: {
      title: 'Study definition',
      description: 'Configure the protocol basics before choosing the randomization method.',
      name: 'Study name',
      namePlaceholder: 'E.g.: Thesis Clinical Trial 2026',
      participants: 'Number of participants',
      arms: 'Number of arms',
      armsOptions: ['2 arms', '3 arms', '4 arms'],
      armNames: 'Arm names',
      armDescription: 'Use clear names for the list, exports, and report.',
      armName: 'Arm name',
      clear: 'Clear',
      next: 'Next',
      back: 'Back'
    },
    technique: {
      title: 'Randomization technique',
      description: 'Choose what will be randomized first. Then select the technique based on randomization and balance units.',
      assisted: 'Assisted choice',
      filters: {
        all: 'All',
        participant: 'Participant',
        stratified: 'Participant + stratum',
        cluster: 'Whole cluster'
      },
      families: {
        basic: ['Basic', 'Individual draw'],
        blocks: ['Blocks', 'Balance during enrollment'],
        stratified: ['Stratified', 'Balance inside subgroups'],
        cluster: ['Cluster', 'One treatment per group']
      }
    },
    parameters: {
      title: 'Parameters and reproducibility',
      description: 'The seed is the key to reproducing the exact same list on another computer.',
      seed: 'Seed',
      generateSeed: 'Generate seed',
      copySeed: 'Copy seed',
      blockSize: 'Block size',
      blockSizes: 'Allowed block sizes',
      clusterSizes: 'Cluster sizes',
      strata: 'Strata',
      clusterStrata: 'Clusters as strata',
      clusterStrataSizes: 'Clusters within strata',
      csvTitle: 'CSV import',
      csvDescription: 'Use templates to fill clusters, strata, or clusters within strata with fewer manual errors.',
      strataColumns: 'Stratification characteristics',
      strataColumnsHelp: 'For stratified methods, import a participant-level list. In preview, select the columns that form composite strata.',
      downloadTemplate: 'Download CSV template',
      importCsv: 'Import CSV',
      csvPreview: 'CSV preview',
      csvCheck: 'Review before applying',
      discard: 'Discard',
      applyCsv: 'Apply CSV',
      generate: 'Generate randomization'
    },
    results: {
      title: 'Result and verification',
      description: 'Export the list and generate the report for methodological review.',
      technique: 'Technique',
      imbalance: 'Imbalance',
      validation: 'Validation',
      auditEvents: 'Audit events',
      chainHead: 'Chain head',
      auditNote: 'Local hash-chained trail for V2 regulatory preparation.',
      armDistribution: 'Distribution by arm',
      arm: 'Arm',
      warnings: 'Warnings',
      noWarnings: 'No validation warnings.',
      visibleSolution: 'Visible solution mode',
      patient: 'Patient',
      order: 'Order',
      block: 'Block',
      cluster: 'Cluster',
      stratum: 'Stratum',
      methods: 'Methods',
      audit: 'Audit',
      manifest: 'Manifest',
      signature: 'Signature',
      report: 'PDF report',
      sequencePreviewNote: 'Showing only the first {shown} of {total} rows on the page to keep navigation light. The full list remains available in CSV, JSON, R, Python, and report exports.',
      largeNWarning: 'For N above 2,000, the list can become long in the browser. Use exports for the full list; virtual scroll should be added in the next UI hardening step.'
    },
    validation: {
      passTitle: 'PASS - list ready for review',
      passBody: 'No methodological alert was detected. Export the list and review signature, report, and parameters before use.',
      warnTitle: 'WARN - review before use',
      warnBody: 'The list was generated, but there is a methodological alert. Review the warnings; changing the seed only helps when the problem is not structural.'
    },
    localData: {
      eyebrow: 'Local persistence',
      title: 'Local data and offline queue',
      summary: 'Draft, last result, and offline events stay only in this browser.',
      draft: 'Draft',
      lastResult: 'Last result',
      queue: 'Offline queue',
      saved: 'Saved',
      empty: 'Empty',
      events: 'events',
      exportQueue: 'Export queue',
      clearQueue: 'Clear queue',
      clearLocalData: 'Clear local data',
      queueCleared: 'Offline queue cleared.',
      localDataCleared: 'Local draft and last result were cleared.',
      queueExported: 'Offline queue exported.'
    },
    operational: {
      securityEyebrow: 'Operational security',
      modeTitle: 'Use mode',
      modeDescription: 'Use planning to see the full list. Use operational mode to conceal future allocations during enrollment.',
      planningLabel: 'Educational / planning',
      planningHelp: 'Full list visible after generation.',
      operationalLabel: 'Protected operational',
      operationalHelp: 'Future allocations are concealed and each reveal is audited.',
      eyebrow: 'Allocation concealment',
      title: 'Progressive reveal',
      description: 'The full list exists internally, but the interface shows only already revealed allocations.',
      revealed: 'Revealed',
      concealed: 'Concealed',
      seedCommit: 'Seed commit',
      revealNext: 'Reveal next allocation',
      copySeedCommit: 'Copy seed commit',
      exportOperational: 'Operational export',
      exportIntegrity: 'Hash manifest',
      concealedCell: 'Concealed until enrollment',
      fullExportBlocked: 'Operational mode active: full-list export is blocked while allocations remain concealed. Use Operational export.',
      allocationRevealed: 'Allocation revealed and recorded in the audit trail.',
      allRevealed: 'All allocations have already been revealed.',
      commitCopied: 'Seed commit copied. Register it externally before enrollment.',
      commitInstructions: 'Register this hash outside randFlow before enrollment: dated institutional email, OSF, ClinicalTrials.gov, or an approved study repository.',
      verificationEyebrow: 'Independent verification',
      verificationTitle: 'Reproduce signature',
      verificationDescription: 'Paste JSON with technique, parameters, and expected signature to regenerate the list without relying on browser state.',
      fillVerification: 'Fill with current result',
      runVerification: 'Verify',
      verificationPass: 'PASS - signature reproduced.',
      verificationFail: 'FAIL - signature mismatch.',
      verificationError: 'Could not verify the provided JSON.',
      sessionStorageNote: 'In operational mode, the last result is kept only in the current session, not persistent local storage.'
    },
    status: {
      generated: 'Randomization generated successfully.',
      signatureCopied: 'Experiment signature copied.',
      seedCopied: 'Seed copied.',
      csvLoaded: 'CSV loaded for preview. Review the table before applying it.',
      csvApplied: 'CSV applied to the active method fields.',
      restoredDraft: 'Local draft restored.',
      restoredResult: 'Last local result restored.',
      languageChanged: 'Language changed.',
      needCsv: 'Import a CSV before applying it.',
      csvUnavailable: 'CSV import is available for cluster and stratified methods only.',
      needResult: 'Generate a randomization before exporting.',
      participantRange: 'Use a participant count between 1 and 10000.',
      armRange: 'Use between 2 and 4 study arms.'
    },
    preview: {
      selectedMethod: 'Selected method',
      randomizes: 'Randomizes',
      balances: 'Balances',
      whenUse: 'When to use',
      caution: 'Caution',
      methodPreview: 'Method preview',
      participants: 'Participants',
      arms: 'Arms',
      randomizedUnit: 'Randomized unit',
      balance: 'Balance',
      pendingSeed: 'Pending seed',
      currentSeed: 'Current seed'
    },
    techniques: {
      simple: ['Simple', 'Randomizes each participant independently. May create imbalance in small samples.', 'Participant', 'No forced balance'],
      blocked: ['Fixed blocks', 'Maintains balance at each complete block. Simpler, but more predictable.', 'Participant', 'Fixed block'],
      'random-block': ['Random blocks', 'Balances by blocks while varying size to reduce operational predictability.', 'Participant', 'Variable block'],
      'stratified-blocked': ['Stratified fixed blocks', 'Repeats fixed blocks within each stratum, such as center, sex, or severity.', 'Participant', 'Stratum + fixed block'],
      'stratified-random-block': ['Stratified random blocks', 'Repeats variable blocks within each stratum to balance with lower predictability.', 'Participant', 'Stratum + variable block'],
      'cluster-stratum': ['Cluster as stratum', 'The center organizes balance, but participants in the same center may receive different arms.', 'Participant', 'Center/cluster'],
      clustered: ['Cluster', 'Randomizes intact groups. All participants in a cluster receive the same arm.', 'Cluster', 'Number of clusters'],
      'cluster-stratified': ['Cluster stratified', 'Randomizes whole clusters within strata while preserving one arm per cluster.', 'Cluster', 'Cluster stratum']
    },
    tags: {
      randomizes: 'Randomizes',
      balances: 'Balances'
    }
  },
  es: {
    locale: 'es',
    appTitle: 'Aleatorización para ensayos clínicos',
    darkMode: 'Modo oscuro',
    lightMode: 'Modo claro',
    validationReportLink: 'Versión 2 Validada',
    userManualLink: 'Manual',
    steps: ['Estudio', 'Técnica', 'Parámetros', 'Resultado'],
    study: {
      title: 'Definición del estudio',
      description: 'Configure la base del protocolo antes de elegir el método de aleatorización.',
      name: 'Nombre del estudio',
      namePlaceholder: 'Ej.: Ensayo Clínico Tesis 2026',
      participants: 'Número de participantes',
      arms: 'Número de brazos',
      armsOptions: ['2 brazos', '3 brazos', '4 brazos'],
      armNames: 'Nombres de los brazos',
      armDescription: 'Use nombres claros para la lista, exports e informe.',
      armName: 'Nombre del brazo',
      clear: 'Limpiar',
      next: 'Avanzar',
      back: 'Volver'
    },
    technique: {
      title: 'Técnica de aleatorización',
      description: 'Elija primero qué será aleatorizado. Luego seleccione la técnica según la unidad de aleatorización y balance.',
      assisted: 'Elección asistida',
      filters: {
        all: 'Todos',
        participant: 'Participante',
        stratified: 'Participante + estrato',
        cluster: 'Cluster completo'
      },
      families: {
        basic: ['Básica', 'Sorteo individual'],
        blocks: ['Bloques', 'Equilibrio durante la inclusión'],
        stratified: ['Estratificada', 'Equilibrio dentro de subgrupos'],
        cluster: ['Cluster', 'Un tratamiento por grupo']
      }
    },
    parameters: {
      title: 'Parámetros y reproducibilidad',
      description: 'La seed es la clave para reproducir exactamente la misma lista en otra computadora.',
      seed: 'Seed',
      generateSeed: 'Generar seed',
      copySeed: 'Copiar seed',
      blockSize: 'Tamaño del bloque',
      blockSizes: 'Tamaños de bloque permitidos',
      clusterSizes: 'Tamaños de clusters',
      strata: 'Estratos',
      clusterStrata: 'Clusters como estratos',
      clusterStrataSizes: 'Clusters dentro de estratos',
      csvTitle: 'Importación CSV',
      csvDescription: 'Use plantillas para completar clusters, estratos o clusters dentro de estratos con menos error manual.',
      strataColumns: 'Características para estratificar',
      strataColumnsHelp: 'Para métodos estratificados, importe una lista por participante. En el preview, marque las columnas que formarán los estratos compuestos.',
      downloadTemplate: 'Descargar plantilla CSV',
      importCsv: 'Importar CSV',
      csvPreview: 'Preview del CSV',
      csvCheck: 'Revisión antes de aplicar',
      discard: 'Descartar',
      applyCsv: 'Aplicar CSV',
      generate: 'Generar aleatorización'
    },
    results: {
      title: 'Resultado y verificación',
      description: 'Exporte la lista y genere el informe para revisión metodológica.',
      technique: 'Técnica',
      imbalance: 'Desequilibrio',
      validation: 'Validación',
      auditEvents: 'Eventos audit',
      chainHead: 'Chain head',
      auditNote: 'Trazabilidad local encadenada por hash para preparación regulatoria V2.',
      armDistribution: 'Distribución por brazo',
      arm: 'Brazo',
      warnings: 'Avisos',
      noWarnings: 'Sin avisos de validación.',
      visibleSolution: 'Modo solución visible',
      patient: 'Paciente',
      order: 'Orden',
      block: 'Bloque',
      cluster: 'Cluster',
      stratum: 'Estrato',
      methods: 'Método',
      audit: 'Auditoría',
      manifest: 'Manifiesto',
      signature: 'Firma',
      report: 'Informe PDF',
      sequencePreviewNote: 'Se muestran solo las primeras {shown} filas de {total} en la página para mantener la navegación ligera. La lista completa permanece disponible en los exports CSV, JSON, R, Python e informe.',
      largeNWarning: 'Para N sobre 2.000, la lista puede ser larga en el navegador. Use los exports para la lista completa; virtual scroll debe entrar en el próximo endurecimiento de UI.'
    },
    validation: {
      passTitle: 'PASS - lista lista para revisión',
      passBody: 'No se detectó alerta metodológica. Exporte la lista y revise firma, informe y parámetros antes de usar.',
      warnTitle: 'WARN - revisar antes de usar',
      warnBody: 'La lista fue generada, pero hay una alerta metodológica. Revise los avisos; cambiar la seed solo ayuda cuando el problema no es estructural.'
    },
    localData: {
      eyebrow: 'Persistencia local',
      title: 'Datos locales y fila offline',
      summary: 'Borrador, último resultado y eventos offline quedan solo en este navegador.',
      draft: 'Borrador',
      lastResult: 'Último resultado',
      queue: 'Fila offline',
      saved: 'Guardado',
      empty: 'Vacío',
      events: 'eventos',
      exportQueue: 'Exportar fila',
      clearQueue: 'Limpiar fila',
      clearLocalData: 'Limpiar datos locales',
      queueCleared: 'Fila offline limpia.',
      localDataCleared: 'Borrador y último resultado locales fueron limpiados.',
      queueExported: 'Fila offline exportada.'
    },
    operational: {
      securityEyebrow: 'Seguridad operacional',
      modeTitle: 'Modo de uso',
      modeDescription: 'Use planificación para ver la lista completa. Use modo operacional para ocultar asignaciones futuras durante el enrolamiento.',
      planningLabel: 'Educacional / planificación',
      planningHelp: 'Lista completa visible después de generar.',
      operationalLabel: 'Operacional protegido',
      operationalHelp: 'Asignaciones futuras ocultas y cada revelación auditada.',
      eyebrow: 'Allocation concealment',
      title: 'Revelación progresiva',
      description: 'La lista completa existe internamente, pero la interfaz muestra solo asignaciones reveladas.',
      revealed: 'Reveladas',
      concealed: 'Ocultas',
      seedCommit: 'Seed commit',
      revealNext: 'Revelar próxima asignación',
      copySeedCommit: 'Copiar seed commit',
      exportOperational: 'Export operacional',
      exportIntegrity: 'Manifiesto de hashes',
      concealedCell: 'Oculto hasta enrolamiento',
      fullExportBlocked: 'Modo operacional activo: export de lista completa bloqueado mientras existan asignaciones ocultas. Use Export operacional.',
      allocationRevealed: 'Asignación revelada y registrada en la auditoría.',
      allRevealed: 'Todas las asignaciones ya fueron reveladas.',
      commitCopied: 'Seed commit copiado. Regístrelo externamente antes del enrolamiento.',
      commitInstructions: 'Registre este hash fuera de randFlow antes del enrolamiento: email institucional fechado, OSF, ClinicalTrials.gov o repositorio aprobado.',
      verificationEyebrow: 'Verificación independiente',
      verificationTitle: 'Reproducir firma',
      verificationDescription: 'Pegue JSON con técnica, parámetros y firma esperada para regenerar la lista sin depender del estado del navegador.',
      fillVerification: 'Completar con resultado actual',
      runVerification: 'Verificar',
      verificationPass: 'PASS - firma reproducida.',
      verificationFail: 'FAIL - firma divergente.',
      verificationError: 'No fue posible verificar el JSON informado.',
      sessionStorageNote: 'En modo operacional, el último resultado queda solo en la sesión actual, no en almacenamiento local persistente.'
    },
    status: {
      generated: 'Aleatorización generada con éxito.',
      signatureCopied: 'Firma del experimento copiada.',
      seedCopied: 'Seed copiada.',
      csvLoaded: 'CSV cargado para preview. Revise la tabla antes de aplicar.',
      csvApplied: 'CSV aplicado a los campos del método activo.',
      restoredDraft: 'Borrador local restaurado.',
      restoredResult: 'Último resultado local restaurado.',
      languageChanged: 'Idioma cambiado.',
      needCsv: 'Importe un CSV antes de aplicar.',
      csvUnavailable: 'La importación CSV solo está disponible para métodos con clusters o estratos.',
      needResult: 'Genere una aleatorización antes de exportar.',
      participantRange: 'Use un número de participantes entre 1 y 10000.',
      armRange: 'Use entre 2 y 4 brazos.'
    },
    preview: {
      selectedMethod: 'Método seleccionado',
      randomizes: 'Aleatoriza',
      balances: 'Balancea',
      whenUse: 'Cuándo usar',
      caution: 'Cuidado',
      methodPreview: 'Preview metodológico',
      participants: 'Participantes',
      arms: 'Brazos',
      randomizedUnit: 'Unidad aleatorizada',
      balance: 'Balance',
      pendingSeed: 'Seed pendiente',
      currentSeed: 'Seed actual'
    },
    techniques: {
      simple: ['Simple', 'Aleatoriza cada participante de forma independiente. Puede desequilibrar muestras pequeñas.', 'Participante', 'No fuerza balance'],
      blocked: ['Bloques fijos', 'Mantiene equilibrio en cada bloque completo. Más simple, pero más predecible.', 'Participante', 'Bloque fijo'],
      'random-block': ['Bloques aleatorios', 'Balancea por bloques variando el tamaño para reducir la previsibilidad operacional.', 'Participante', 'Bloque variable'],
      'stratified-blocked': ['Estratificada por bloques fijos', 'Repite bloques fijos dentro de cada estrato, como centro, sexo o gravedad.', 'Participante', 'Estrato + bloque fijo'],
      'stratified-random-block': ['Estratificada por bloques aleatorios', 'Repite bloques variables dentro de cada estrato para balancear con menor previsibilidad.', 'Participante', 'Estrato + bloque variable'],
      'cluster-stratum': ['Cluster como estrato', 'El centro organiza el balance, pero participantes del mismo centro pueden recibir brazos diferentes.', 'Participante', 'Centro/cluster'],
      clustered: ['Cluster', 'Aleatoriza grupos intactos. Todos los participantes de un cluster reciben el mismo brazo.', 'Cluster', 'Cantidad de clusters'],
      'cluster-stratified': ['Cluster estratificado', 'Aleatoriza clusters completos dentro de estratos preservando un único brazo por cluster.', 'Cluster', 'Estrato de clusters']
    },
    tags: {
      randomizes: 'Aleatoriza',
      balances: 'Balancea'
    }
  }
};

function normalizeLanguage(language) {
  return SUPPORTED_LANGUAGES.includes(language) && Object.prototype.hasOwnProperty.call(TRANSLATIONS, language)
    ? language
    : DEFAULT_LANGUAGE;
}

function getTranslations(language) {
  return TRANSLATIONS[normalizeLanguage(language)];
}

module.exports = {
  LANGUAGES,
  TRANSLATIONS,
  getTranslations,
  normalizeLanguage
};
